<template>
  <div class="registered-user-accounts page-wrapper">
    <el-card shadow="hover">
      <template slot="header">
        <div class="card-header">
          <div>
            <span class="card-title">用户管理</span>
            <p class="card-sub">
              小程序用户与后台管理员统一维护：账号类型含普通用户 / 管理员 / 超级管理员 / 收银员；管理员微信登录后可直接团购核销，无需二次账密登录
            </p>
          </div>
          <el-input
            v-model="keyword"
            placeholder="搜索账号名 / 手机 / 邮箱"
            clearable
            size="small"
            style="width: 260px"
          />
        </div>
      </template>

      <div class="role-filter-bar">
        <span class="filter-label">账号类型</span>
        <button
          v-for="tab in roleFilterTabs"
          :key="tab.value"
          type="button"
          class="role-filter-chip"
          :class="{ active: roleFilter === tab.value }"
          @click="roleFilter = tab.value"
        >
          {{ tab.label }}
          <em>{{ tabCount(tab.value) }}</em>
        </button>
      </div>

      <el-table :data="filteredList" border v-loading="loading" style="width: 100%">
        <el-table-column type="index" label="#" width="56" align="center" />
        <el-table-column prop="id" label="用户ID" min-width="168" show-overflow-tooltip />
        <el-table-column prop="username" label="用户账号" min-width="120" />
        <el-table-column prop="phone" label="手机号" min-width="130" />
        <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip />
        <el-table-column label="账号类型" width="120" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="accountRoleTagType(scope.row.account_role)">
              {{ accountRoleLabel(scope.row.account_role) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="权限范围" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ permissionScopeText(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column label="微信绑定" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="wechatBound(scope.row) ? 'success' : 'info'">
              {{ wechatBound(scope.row) ? '已绑定' : '未绑定' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="绑定员工" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.employee_name">{{ scope.row.employee_name }}</span>
            <span v-else class="text-muted">未绑定</span>
          </template>
        </el-table-column>
        <el-table-column label="注册时间" min-width="160">
          <template slot-scope="scope">{{ $formatDateTime(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="最后登录" min-width="160">
          <template slot-scope="scope">{{ $formatDateTime(scope.row.last_login_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" class="btn-secondary" @click="openEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="canAdminDeleteSession"
              size="mini"
              class="btn-secondary danger"
              @click="removeUser(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      title="编辑用户账号"
      custom-class="rua-edit-dialog"
      :visible.sync="editVisible"
      width="580px"
      append-to-body
      destroy-on-close
    >
      <el-form :model="editForm" label-width="108px">
        <el-form-item label="用户ID">
          <el-input :value="editForm.id" disabled />
        </el-form-item>
        <el-form-item label="账号名">
          <el-input v-model="editForm.username" maxlength="50" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="editForm.phone" maxlength="20" />
          <div class="form-tip">与员工手机号一致时，微信快捷登录可自动关联员工（若已手动绑定则优先使用绑定关系）</div>
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="editForm.email" maxlength="200" />
        </el-form-item>
        <el-form-item label="账号类型">
          <el-select v-model="editForm.account_role" placeholder="选择账号类型" style="width: 100%">
            <el-option
              v-for="opt in roleOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
              :disabled="opt.value === '超级管理员' && !isSuperAdminSession"
            />
          </el-select>
          <div class="form-tip">
            管理员 / 超级管理员 / 收银员：微信登录后可直接团购核销；普通用户为顾客身份
          </div>
        </el-form-item>
        <el-form-item v-if="editForm.account_role === '管理员' || editForm.account_role === '收银员'" label="功能权限">
          <el-checkbox-group v-model="editForm.permissions">
            <el-checkbox v-for="p in permissionOptions" :key="p" :label="p">{{ p }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="绑定员工">
          <el-select
            v-model="editForm.employee_id"
            filterable
            clearable
            placeholder="选择员工（可选）"
            style="width: 100%"
            @change="onEmployeeChange"
          >
            <el-option
              v-for="emp in employees"
              :key="emp.id"
              :label="employeeOptionLabel(emp)"
              :value="emp.id"
            />
          </el-select>
          <div class="form-tip">绑定后将关联员工档案；选择员工时可自动带入其角色与权限，保存后该用户微信登录即生效</div>
        </el-form-item>
        <el-form-item label="收货地址">
          <el-input v-model="editForm.address" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="editForm.remark" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="邮件免打扰">
          <el-switch v-model="editForm.email_do_not_disturb" active-text="开启" inactive-text="关闭" />
          <div class="form-tip">开启后不接收系统邮件（验证码除外）</div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="rua-edit-footer">
        <el-button class="rua-btn-cancel" @click="editVisible = false">取消</el-button>
        <el-button
          type="primary"
          class="rua-btn-save"
          icon="el-icon-check"
          :loading="saving"
          @click="submitEdit"
        >保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'
import { canAdminDelete, isSuperAdminUser } from '../utils/auth'

const OFFLINE_GUEST_PHONE = '__eatin_offline_guest__'

const PERMISSION_OPTIONS = [
  '订单管理',
  '商品管理',
  '客户管理',
  '兑换券管理',
  '随心订管理',
  '员工管理',
  '骑手管理',
  '数据统计'
]

const EMPLOYEE_ROLE_TO_ACCOUNT = {
  超级管理员: '超级管理员',
  管理员: '管理员',
  普通账号: '普通账号',
  员工: '员工',
  收银员: '收银员'
}

export default {
  name: 'RegisteredUserAccounts',
  data() {
    return {
      loading: false,
      saving: false,
      keyword: '',
      roleFilter: '全部',
      list: [],
      employees: [],
      editVisible: false,
      permissionOptions: PERMISSION_OPTIONS,
      editForm: {
        id: '',
        username: '',
        phone: '',
        email: '',
        address: '',
        remark: '',
        account_role: '普通账号',
        permissions: [],
        employee_id: null,
        email_do_not_disturb: false
      }
    }
  },
  computed: {
    canAdminDeleteSession() {
      return canAdminDelete()
    },
    isSuperAdminSession() {
      return isSuperAdminUser()
    },
    roleFilterTabs() {
      return [
        { label: '全部', value: '全部' },
        { label: '普通用户', value: '普通账号' },
        { label: '管理员', value: '管理员' },
        { label: '超级管理员', value: '超级管理员' },
        { label: '收银员', value: '收银员' }
      ]
    },
    roleOptions() {
      return [
        { label: '普通用户', value: '普通账号' },
        { label: '管理员', value: '管理员' },
        { label: '超级管理员', value: '超级管理员' },
        { label: '收银员', value: '收银员' },
        { label: '员工（兼容）', value: '员工' }
      ]
    },
    registeredRows() {
      return this.list.filter((u) => this.isRegisteredUser(u))
    },
    filteredList() {
      const kw = String(this.keyword || '').trim().toLowerCase()
      let rows = this.registeredRows
      if (this.roleFilter && this.roleFilter !== '全部') {
        rows = rows.filter((u) => this.roleMatchesFilter(u.account_role, this.roleFilter))
      }
      if (!kw) return rows
      return rows.filter((u) => {
        const hay = [u.username, u.phone, u.email, u.id, u.employee_name, u.account_role]
          .map((x) => String(x || '').toLowerCase())
          .join(' ')
        return hay.includes(kw)
      })
    }
  },
  mounted() {
    this.loadUsers()
    this.loadEmployees()
  },
  methods: {
    roleMatchesFilter(role, filter) {
      const r = String(role || '').trim()
      if (filter === '普通账号') return r === '普通账号' || r === '员工' || !r
      return r === filter
    },
    tabCount(filter) {
      if (filter === '全部') return this.registeredRows.length
      return this.registeredRows.filter((u) => this.roleMatchesFilter(u.account_role, filter)).length
    },
    accountRoleLabel(role) {
      const r = String(role || '').trim()
      if (r === '普通账号' || r === '员工' || !r) return '普通用户'
      return r
    },
    accountRoleTagType(role) {
      const r = String(role || '').trim()
      if (r === '超级管理员') return 'danger'
      if (r === '管理员') return ''
      if (r === '收银员') return 'warning'
      return 'info'
    },
    permissionScopeText(row) {
      const r = String((row && row.account_role) || '').trim()
      if (r === '超级管理员') return '全部权限'
      const perms = Array.isArray(row.permissions) ? row.permissions.filter(Boolean) : []
      if (r === '管理员' || r === '收银员') {
        return perms.length ? perms.join('、') : '订单管理'
      }
      return '—'
    },
    isRegisteredUser(u) {
      if (!u) return false
      const phone = String(u.phone || '').trim()
      if (phone === OFFLINE_GUEST_PHONE) return false
      const email = String(u.email || '').trim()
      const openid = String(u.openid || '').trim()
      const hasWechat = openid && !openid.startsWith('test_')
      const hasPassword = !!u.password
      const role = String(u.account_role || '').trim()
      if (role === '管理员' || role === '超级管理员' || role === '收银员' || role === '员工') return true
      return !!(email || hasWechat || hasPassword)
    },
    wechatBound(u) {
      const openid = String((u && u.openid) || '').trim()
      return !!(openid && !openid.startsWith('test_'))
    },
    employeeOptionLabel(emp) {
      const id = emp.custom_employee_id || emp.id
      const name = emp.name || emp.username || '未命名'
      const phone = emp.phone ? ` · ${emp.phone}` : ''
      return `${name}（${id}）${phone}`
    },
    permissionsForEmployeeRole(role, perms) {
      const r = String(role || '').trim()
      if (r === '超级管理员') return [...PERMISSION_OPTIONS]
      if (r === '管理员' || r === '收银员') {
        const list = Array.isArray(perms) ? perms.filter(Boolean) : []
        return list.length ? list : ['订单管理']
      }
      return []
    },
    async loadEmployees() {
      try {
        const resp = await this.$api.get('/admin/employees')
        if (resp && resp.code === 200) {
          this.employees = (resp.data || []).map((e) => ({
            id: e.id != null ? e.id : e._id,
            name: e.name,
            username: e.username,
            phone: e.phone,
            role: e.role,
            permissions: e.permissions || [],
            custom_employee_id: e.custom_employee_id || ''
          }))
        }
      } catch (_e) {
        this.employees = []
      }
    },
    async loadUsers() {
      this.loading = true
      try {
        const resp = await this.$api.get('/admin/users')
        if (resp && resp.code === 200) {
          this.list = Array.isArray(resp.data) ? resp.data : []
        } else {
          this.list = []
        }
      } catch (e) {
        this.list = []
        this.$message.error(formatApiError(e, '加载用户列表失败'))
      } finally {
        this.loading = false
      }
    },
    openEdit(row) {
      this.editForm = {
        id: row.id,
        username: row.username || '',
        phone: row.phone || '',
        email: row.email || '',
        address: row.address || '',
        remark: row.remark || '',
        account_role: row.account_role || '普通账号',
        permissions: Array.isArray(row.permissions) ? [...row.permissions] : [],
        employee_id: row.employee_id != null ? Number(row.employee_id) : null,
        email_do_not_disturb: Number(row.email_do_not_disturb) === 1
      }
      this.editVisible = true
    },
    onEmployeeChange(employeeId) {
      if (!employeeId) return
      const emp = this.employees.find((e) => Number(e.id) === Number(employeeId))
      if (!emp) return
      const mappedRole = EMPLOYEE_ROLE_TO_ACCOUNT[emp.role] || '普通账号'
      this.editForm.account_role = mappedRole
      this.editForm.permissions = this.permissionsForEmployeeRole(emp.role, emp.permissions)
      if (emp.phone && !String(this.editForm.phone || '').trim()) {
        this.editForm.phone = emp.phone
      }
      if (emp.name && (!this.editForm.username || /^微信用户/.test(this.editForm.username))) {
        this.editForm.username = emp.name
      }
    },
    async submitEdit() {
      const id = this.editForm.id
      if (!id) return
      this.saving = true
      try {
        const role = this.editForm.account_role || '普通账号'
        const payload = {
          username: String(this.editForm.username || '').trim(),
          phone: String(this.editForm.phone || '').trim(),
          email: String(this.editForm.email || '').trim(),
          address: this.editForm.address || '',
          remark: this.editForm.remark || '',
          account_role: role,
          permissions:
            role === '管理员' || role === '收银员' ? this.editForm.permissions || [] : [],
          employee_id: this.editForm.employee_id != null ? this.editForm.employee_id : null,
          email_do_not_disturb: this.editForm.email_do_not_disturb ? 1 : 0
        }
        const resp = await this.$api.put(`/admin/users/${id}`, payload)
        if (resp && resp.code === 200) {
          this.$message.success('保存成功')
          this.editVisible = false
          await this.loadUsers()
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.saving = false
      }
    },
    removeUser(row) {
      this.$confirm(`确定删除用户「${row.username || row.id}」吗？此操作不可恢复。`, '提示', {
        type: 'warning'
      })
        .then(async () => {
          try {
            const resp = await this.$api.delete(`/admin/users/${row.id}`)
            if (resp && resp.code === 200) {
              this.$message.success('已删除')
              await this.loadUsers()
            } else {
              this.$message.error((resp && resp.message) || '删除失败')
            }
          } catch (e) {
            this.$message.error(formatApiError(e, '删除失败'))
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.registered-user-accounts {
  padding: 24px;
}
.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.card-title {
  font-size: 16px;
  font-weight: 700;
}
.card-sub {
  margin: 6px 0 0;
  font-size: 13px;
  color: #909399;
  max-width: 720px;
  line-height: 1.5;
}
.role-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.filter-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
  margin-right: 4px;
}
.role-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.role-filter-chip em {
  font-style: normal;
  font-size: 12px;
  color: #94a3b8;
}
.role-filter-chip:hover {
  border-color: #2bb673;
  color: #047857;
}
.role-filter-chip.active {
  background: #2bb673;
  border-color: #2bb673;
  color: #fff;
}
.role-filter-chip.active em {
  color: rgba(255, 255, 255, 0.85);
}
.form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
.text-muted {
  color: #c0c4cc;
}
</style>

<style>
/* append-to-body：需非 scoped */
.rua-edit-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.rua-edit-dialog .el-dialog__header {
  padding: 18px 24px 12px;
}

.rua-edit-dialog .el-dialog__title {
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
}

.rua-edit-dialog .el-dialog__body {
  padding: 8px 24px 4px;
}

.rua-edit-dialog .el-dialog__footer {
  padding: 14px 24px 18px !important;
  border-top: 1px solid #eef2f0;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.rua-edit-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.rua-edit-footer .rua-btn-cancel {
  min-width: 96px;
  height: 40px;
  padding: 0 20px;
  margin: 0 !important;
  border-radius: 12px !important;
  border: 1px solid #e2e8f0 !important;
  background: #fff !important;
  color: #64748b !important;
  font-size: 14px;
  font-weight: 600;
  box-shadow: none !important;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.rua-edit-footer .rua-btn-cancel:hover,
.rua-edit-footer .rua-btn-cancel:focus {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
  color: #334155 !important;
  transform: none !important;
  box-shadow: none !important;
}

.rua-edit-footer .rua-btn-save {
  min-width: 112px;
  height: 40px;
  padding: 0 22px;
  margin: 0 !important;
  border-radius: 12px !important;
  border: none !important;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: linear-gradient(135deg, #34d399 0%, #2bb673 52%, #1f9f63 100%) !important;
  box-shadow: 0 8px 18px rgba(43, 182, 115, 0.32) !important;
}

.rua-edit-footer .rua-btn-save:hover,
.rua-edit-footer .rua-btn-save:focus {
  background: linear-gradient(135deg, #2bb673 0%, #22a06b 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(43, 182, 115, 0.38) !important;
}

.rua-edit-footer .rua-btn-save.is-loading {
  transform: none !important;
}
</style>
